/**
 * Toast notification component for DEX UI.
 * Displays auto-dismissing success/error/info messages.
 */

/**
 * Show a toast notification.
 * @param {string} message - Message to display
 * @param {'success' | 'error' | 'info'} [type='success'] - Toast type for styling
 * @param {number} [duration=4000] - Duration in milliseconds before auto-dismiss
 * @returns {HTMLElement} The toast element (allows manual dismissal if needed)
 */
export function showToast(message, type = 'success', duration = 4000) {
  const toast = document.createElement('div');

  // Base classes for positioning and animation
  const baseClasses = 'fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg transition-all duration-300 max-w-sm';

  // Type-specific colors
  const typeClasses = {
    success: 'bg-green-600 text-white',
    error: 'bg-red-600 text-white',
    info: 'bg-gray-800 text-white'
  };

  toast.className = `${baseClasses} ${typeClasses[type] || typeClasses.info}`;
  toast.textContent = message;
  toast.style.zIndex = '9999';
  toast.style.opacity = '0';

  // Append to body
  document.body.appendChild(toast);

  // Fade in using requestAnimationFrame for smooth animation
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
  });

  // Auto dismiss after duration
  const dismissTimeout = setTimeout(() => {
    dismissToast(toast);
  }, duration);

  // Store timeout ID for potential manual dismissal cleanup
  toast._dismissTimeout = dismissTimeout;

  return toast;
}

/**
 * Dismiss a toast notification with fade-out animation.
 * @param {HTMLElement} toast - The toast element to dismiss
 */
function dismissToast(toast) {
  // Clear any pending timeout
  if (toast._dismissTimeout) {
    clearTimeout(toast._dismissTimeout);
  }

  // Fade out
  toast.style.opacity = '0';

  // Remove from DOM after transition completes
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 300);
}
